<template>
  <div class="container">
    <ToolbarComp v-if="showTopBar"></ToolbarComp>

    <result-message></result-message>

    <div class="login-container">
      <img src="@/assets/logo.svg" alt="icono corporativo" class="logo-image" />

      <p v-if="$route.query.expired === 'legacy'" class="login-alert">
        Tu sesión con el admin legacy (admin1) expiró. Vuelve a iniciar sesión con Google.
      </p>

      <p v-if="loginError" class="login-alert login-alert--error">{{ loginError }}</p>

      <p v-if="gisLoading" class="login-loading">Cargando Google…</p>
      <div ref="googleBtnHost" class="google-btn-host" :class="{ 'is-ready': gisReady && !gisLoading }" />

      <LoaderComp v-if="isLoading"></LoaderComp>
    </div>

    <BottomBar v-if="showBottomBar"></BottomBar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ToolbarComp from '@/components/ToolbarComp';
import LoaderComp from '@/components/LoaderComp';
import ResultMessage from '@/components/ResultMessage';
import BottomBar from '@/components/BottomBar';

const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID || '';

function emailFromIdToken(idToken) {
  try {
    const part = String(idToken || '').split('.')[1];
    if (!part) return '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    return String(payload.email || '').toLowerCase();
  } catch {
    return '';
  }
}

function waitForGoogleGis(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }
    const started = Date.now();
    const timer = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(timer);
        resolve(window.google.accounts.id);
        return;
      }
      if (Date.now() - started > timeoutMs) {
        clearInterval(timer);
        reject(new Error('No se pudo cargar Google Identity Services. Revisa bloqueadores o la red.'));
      }
    }, 100);
  });
}

export default {
  name: 'app',
  components: {
    ToolbarComp, LoaderComp, ResultMessage, BottomBar,
  },
  data() {
    return {
      gisReady: false,
      gisLoading: false,
      loginError: '',
      gisInitialized: false,
    };
  },
  computed: {
    showBottomBar() {
      const dontShowIn = ['login', 'intro', 'select-turno', 'select-vehicle'];
      return dontShowIn.indexOf(this.$route.name) === -1;
    },
    showTopBar() {
      const dontShowIn = ['login', 'intro'];
      return dontShowIn.indexOf(this.$route.name) === -1;
    },
    ...mapGetters({
      isLoading: 'form/is-submiting',
    }),
  },
  async mounted() {
    await this.setupGoogleSignIn();
  },
  methods: {
    async setupGoogleSignIn() {
      this.loginError = '';
      if (!GOOGLE_CLIENT_ID) {
        this.loginError = 'Falta VUE_APP_GOOGLE_CLIENT_ID en el entorno.';
        return;
      }
      this.gisLoading = true;
      try {
        const googleId = await waitForGoogleGis();
        googleId.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: this.onGoogleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
          use_fedcm_for_prompt: false,
        });
        this.gisInitialized = true;
        this.gisReady = true;
        this.$nextTick(() => {
          const host = this.$refs.googleBtnHost;
          if (!host) return;
          host.innerHTML = '';
          const width = Math.min(320, Math.max(240, host.clientWidth || 320));
          googleId.renderButton(host, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width,
            locale: 'es',
          });
        });
      } catch (e) {
        console.error(e);
        this.loginError = e.message || 'No se pudo inicializar el login de Google.';
      } finally {
        this.gisLoading = false;
      }
    },
    async onGoogleCredential(response) {
      const idToken = response?.credential;
      if (!idToken) {
        this.loginError = 'Google no devolvió credencial.';
        return;
      }
      const email = emailFromIdToken(idToken);
      if (email) {
        sessionStorage.setItem('estudyoo_admin_django_email', email);
      }
      try {
        await this.$store.dispatch('auth/loginWithGoogle', idToken);
        if (this.$store.state.auth.error) {
          this.loginError = this.$store.state.auth.error || 'Error al iniciar sesión.';
          return;
        }
        const { loginDjangoInBackground } = await import('@/services/djangoAdminAuth');
        await loginDjangoInBackground(email, null);
        const redirect = this.$route.query.redirect;
        if (redirect) {
          this.$router.push({ name: redirect });
        } else {
          this.$router.push({ name: 'home' });
        }
      } catch (e) {
        console.error('Error en la autenticación:', e);
        this.loginError = e.message || 'Error en la autenticación con Google.';
      }
    },
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-height: 100vh;
  background-color: #f6f6fa;
}

.login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
  padding: 40px 32px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 400px;
  text-align: center;
}

.logo-image {
  width: 150px;
  margin-bottom: 28px;
}

.login-alert {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 16px;
  text-align: left;
  width: 100%;
}

.login-alert--error {
  background: #fee2e2;
  border-color: #ef4444;
  color: #991b1b;
}

.login-loading {
  margin: 0 0 12px;
  font-size: 14px;
  color: #6b7280;
}

.google-btn-host {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 44px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.google-btn-host.is-ready {
  opacity: 1;
}

.google-btn-host :deep(div) {
  margin: 0 auto;
}
</style>
